import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Manga, TierGrade, TIER_CONFIGS, TIER_ORDER } from '../types/manga';
import { formatImageUrl, DEFAULT_COVER_PLACEHOLDER, getFlagUrl, formatRelativeThai } from '../utils/helpers';
import {
  DiscoverResult,
  DiscoverSite,
  DiscoverSiteName,
  DISCOVER_SITES,
  searchSite,
  fetchSeriesLatest,
  findTrackedManga,
  buildMangaFromDiscover,
  splitTitle,
} from '../services/discover';
import {
  Search,
  X,
  Loader2,
  Plus,
  Check,
  ExternalLink,
  RefreshCw,
  Sparkles,
  Flame,
  Star,
  Trophy,
  Clock,
  Shuffle,
  LucideIcon,
} from 'lucide-react';

interface DiscoverPageProps {
  mangaList: Manga[];
  onAddManga: (manga: Manga[]) => void;
}

type TypeFilter = 'all' | 'Manhwa' | 'Manhua' | 'Manga';
const TYPE_FILTERS: { id: TypeFilter; label: string }[] = [
  { id: 'all', label: 'ทุกประเภท' },
  { id: 'Manhwa', label: 'เกาหลี' },
  { id: 'Manhua', label: 'จีน' },
  { id: 'Manga', label: 'ญี่ปุ่น' },
];

const GRADES = TIER_ORDER;
const NATIONALITIES = ['🇰🇷', '🇨🇳', '🇯🇵'];
const SITE_PREF_KEY = 'manga_discover_site';
const ALL_SECTIONS = 'all';

interface SectionState {
  items: DiscoverResult[];
  loading: boolean;
  loaded: boolean;
}

interface ZoneTheme {
  icon: LucideIcon;
  hint: string;
  /** Tinted icon badge in the section header */
  badge: string;
}

// Each kind of homepage section gets its own icon colour, so while scrolling it's clear which group a card is in
const ZONE_THEMES: Record<'ranking' | 'latest' | 'popular' | 'picks' | 'search', ZoneTheme> = {
  ranking: {
    icon: Trophy,
    hint: 'จัดอันดับตามยอดอ่าน',
    badge: 'bg-amber-400/10 text-amber-300',
  },
  latest: {
    icon: Clock,
    hint: 'เรื่องที่เพิ่งลงตอนใหม่',
    badge: 'bg-emerald-400/10 text-emerald-300',
  },
  popular: {
    icon: Flame,
    hint: 'เรื่องยอดนิยมของเว็บ',
    badge: 'bg-rose-400/10 text-rose-300',
  },
  picks: {
    icon: Sparkles,
    hint: 'เว็บคัดมาแนะนำ',
    badge: 'bg-violet-400/10 text-violet-300',
  },
  search: {
    icon: Search,
    hint: 'ผลการค้นหา',
    badge: 'bg-sky-400/10 text-sky-300',
  },
};

function zoneTheme(sectionId: string, ranked?: boolean): ZoneTheme {
  if (sectionId === 'search') return ZONE_THEMES.search;
  if (ranked || sectionId.startsWith('hot') || sectionId === 'trending') return ZONE_THEMES.ranking;
  if (sectionId.startsWith('latest') || sectionId.endsWith('-updates')) return ZONE_THEMES.latest;
  if (sectionId === 'popular') return ZONE_THEMES.popular;
  if (sectionId === 'random') return { ...ZONE_THEMES.picks, icon: Shuffle, hint: 'สุ่มเรื่องมาแนะนำ' };
  return ZONE_THEMES.picks;
}

function readSitePref(): DiscoverSiteName {
  try {
    const v = localStorage.getItem(SITE_PREF_KEY);
    if (DISCOVER_SITES.some((s) => s.name === v)) return v as DiscoverSiteName;
  } catch {}
  return DISCOVER_SITES[0].name;
}

const DiscoverCover: React.FC<{ src?: string; alt: string }> = ({ src, alt }) => {
  const [failed, setFailed] = useState(false);
  return (
    <img
      src={failed ? DEFAULT_COVER_PLACEHOLDER : formatImageUrl(src)}
      alt={alt}
      onError={() => setFailed(true)}
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      className="w-full h-full object-cover object-center"
    />
  );
};

export const DiscoverPage: React.FC<DiscoverPageProps> = ({ mangaList, onAddManga }) => {
  const [siteName, setSiteName] = useState<DiscoverSiteName>(readSitePref);
  const [sectionBySite, setSectionBySite] = useState<Record<string, string>>({});
  const [sections, setSections] = useState<Record<string, SectionState>>({});
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState<{ items: DiscoverResult[]; loading: boolean } | null>(null);
  const [adding, setAdding] = useState<DiscoverResult[] | null>(null);
  const searchSeqRef = useRef(0);
  const [fetchedLatest, setFetchedLatest] = useState<Record<string, number>>({});
  const requestedLatestRef = useRef(new Set<string>());

  const site: DiscoverSite = DISCOVER_SITES.find((s) => s.name === siteName) || DISCOVER_SITES[0];
  // 'all' shows the site's whole homepage (pages 1-3) section by section, like the site itself
  const sectionId = sectionBySite[site.name] || ALL_SECTIONS;
  const visibleSections =
    sectionId === ALL_SECTIONS ? site.sections : site.sections.filter((s) => s.id === sectionId);
  const trimmed = query.trim();
  const isSearching = trimmed.length >= 2;

  const loadSection = useCallback(async (target: DiscoverSite, sectionIdToLoad: string, force: boolean) => {
    const sec = target.sections.find((s) => s.id === sectionIdToLoad);
    if (!sec) return;
    const key = `${target.name}|${sec.id}`;
    setSections((prev) => ({
      ...prev,
      [key]: { items: prev[key]?.items || [], loading: true, loaded: prev[key]?.loaded || false },
    }));
    const items = await sec.load(force).catch(() => [] as DiscoverResult[]);
    setSections((prev) => ({ ...prev, [key]: { items, loading: false, loaded: true } }));
  }, []);

  // Load every visible section the first time it is shown (pages share one fetch per URL)
  useEffect(() => {
    for (const sec of visibleSections) {
      const state = sections[`${site.name}|${sec.id}`];
      if (!state || (!state.loaded && !state.loading)) loadSection(site, sec.id, false);
    }
  }, [site.name, sectionId]);

  // Section headers stick right under the app header, whose height changes when its buttons wrap
  useEffect(() => {
    const header = document.querySelector('header');
    if (!header) return;
    const apply = () =>
      document.documentElement.style.setProperty('--app-header-h', `${Math.round(header.getBoundingClientRect().height)}px`);
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(SITE_PREF_KEY, site.name);
    } catch {}
  }, [site.name]);

  // Debounced search inside the selected site; a newer query discards answers to older ones
  useEffect(() => {
    if (!isSearching) {
      searchSeqRef.current++;
      setSearch(null);
      return;
    }
    const seq = ++searchSeqRef.current;
    const timer = setTimeout(() => {
      setSearch({ items: [], loading: true });
      searchSite(site.name, trimmed).then((items) => {
        if (searchSeqRef.current === seq) setSearch({ items, loading: false });
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [trimmed, isSearching, site.name]);

  // Rankings and sliders don't list a chapter. Take it from another section that lists the same
  // series, else from the series page (looked up below).
  const knownLatest = new Map<string, number>();
  for (const st of Object.values(sections)) {
    for (const r of st.items) if (r.latest > (knownLatest.get(r.url) || 0)) knownLatest.set(r.url, r.latest);
  }
  const withLatest = (r: DiscoverResult): DiscoverResult =>
    r.latest > 0 ? r : { ...r, latest: knownLatest.get(r.url) || fetchedLatest[r.url] || 0 };
  const byType = (items: DiscoverResult[]) =>
    (typeFilter === 'all' ? items : items.filter((r) => r.type === typeFilter)).map(withLatest);
  // Search results show as one block; otherwise one block per homepage section
  const blocks: { id: string; label: string; ranked?: boolean; items: DiscoverResult[] }[] = isSearching
    ? [{ id: 'search', label: `ผลค้นหา "${trimmed}"`, items: byType(search?.items || []) }]
    : visibleSections.map((sec) => ({
        id: sec.id,
        label: sec.label,
        ranked: sec.ranked,
        items: byType(sections[`${site.name}|${sec.id}`]?.items || []),
      }));
  const loading = isSearching
    ? !search || search.loading
    : visibleSections.some((sec) => {
        const st = sections[`${site.name}|${sec.id}`];
        return !st || st.loading;
      });
  // The same series appears in several sections; count and bulk-add each one once
  const unique = new Map<string, DiscoverResult>();
  for (const b of blocks) for (const r of b.items) if (!unique.has(r.url)) unique.set(r.url, r);
  const notTracked = [...unique.values()].filter((r) => !findTrackedManga(r, mangaList));
  const siteTotal = (() => {
    const urls = new Set<string>();
    for (const sec of site.sections) for (const r of sections[`${site.name}|${sec.id}`]?.items || []) urls.add(r.url);
    return urls.size;
  })();
  const refresh = () => visibleSections.forEach((sec) => loadSection(site, sec.id, true));

  // Look up the chapters still missing, a few series pages at a time
  const missing = loading
    ? []
    : [...unique.values()].filter(
        (r) => !r.latest && r.site !== '1668manga.com' && !requestedLatestRef.current.has(r.url)
      );
  const missingKey = missing.map((r) => r.url).join('|');
  useEffect(() => {
    if (missing.length === 0) return;
    const queue = [...missing];
    queue.forEach((r) => requestedLatestRef.current.add(r.url));
    const worker = async () => {
      while (queue.length > 0) {
        const r = queue.shift() as DiscoverResult;
        const n = await fetchSeriesLatest(r).catch(() => 0);
        if (n > 0) setFetchedLatest((prev) => ({ ...prev, [r.url]: n }));
      }
    };
    for (let i = 0; i < 4; i++) worker();
  }, [missingKey]);

  return (
    <main className="flex-1 w-full max-w-7xl mx-auto px-3 sm:px-6 pt-4 sm:pt-6 pb-28 sm:pb-10">
      {/* Site tabs + search in the selected site */}
      <div className="flex flex-col md:flex-row gap-2.5 md:gap-3">
        <div className="segmented shrink-0 overflow-x-auto scrollbar-none">
          {DISCOVER_SITES.map((s) => (
            <button
              key={s.name}
              type="button"
              onClick={() => setSiteName(s.name)}
              className={`segmented-item md:flex-none px-3 sm:px-4 text-xs sm:text-sm ${
                s.name === site.name ? 'segmented-item-on' : ''
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${s.dotClass}`} />
              {s.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 flex items-center">
          <Search className="absolute left-3 w-4 h-4 text-zinc-500 pointer-events-none" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`ค้นหาใน ${site.label}`}
            className="field h-9 pl-9 pr-9 py-0"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              title="ล้างคำค้นหา"
              className="absolute right-1.5 p-1.5 rounded-md text-zinc-500 hover:text-zinc-100"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Section chips (scroll sideways on phones), "ทั้งหมด" = the whole site page */}
      <div
        className={`flex items-center gap-1.5 sm:gap-2 mt-4 overflow-x-auto scrollbar-none -mx-3 px-3 sm:mx-0 sm:px-0 sm:flex-wrap ${
          isSearching ? 'opacity-40 pointer-events-none' : ''
        }`}
      >
        {[{ id: ALL_SECTIONS, label: 'ทั้งหมด' }, ...site.sections].map((s) => {
          const active = s.id === sectionId;
          const count =
            s.id === ALL_SECTIONS ? siteTotal || undefined : sections[`${site.name}|${s.id}`]?.items.length;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => setSectionBySite((prev) => ({ ...prev, [site.name]: s.id }))}
              className={`chip ${active ? 'chip-on' : 'chip-off'}`}
            >
              {s.id === ALL_SECTIONS && <Flame className="w-3.5 h-3.5 text-orange-400" />}
              {s.label}
              {count !== undefined && (
                <span className={`tabular-nums ${active ? 'text-zinc-500' : 'text-zinc-600'}`}>{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Type filter */}
      <div className="flex items-center gap-1.5 sm:gap-2 mt-2">
        {TYPE_FILTERS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTypeFilter(t.id)}
            className={`chip ${typeFilter === t.id ? 'chip-on' : 'chip-off'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Count + site link + bulk add + refresh */}
      <div className="flex items-center justify-between gap-2 mt-5">
        <div className="text-xs text-zinc-500 flex items-center gap-1.5 min-w-0">
          {loading && <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />}
          <span className="whitespace-nowrap">
            <span className="text-zinc-200 font-medium tabular-nums">{unique.size}</span> เรื่อง ·
          </span>
          <a
            href={site.homeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 hover:text-zinc-200 transition-colors min-w-0"
          >
            <span className="truncate">{site.name}</span>
            <ExternalLink className="w-3 h-3 shrink-0" />
          </a>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            disabled={notTracked.length === 0}
            onClick={() => setAdding(notTracked)}
            className="btn btn-sm btn-secondary"
          >
            <Plus className="w-3.5 h-3.5" />
            เพิ่มทั้งหมด ({notTracked.length})
          </button>
          {!isSearching && (
            <button type="button" onClick={refresh} disabled={loading} title="โหลดใหม่" className="icon-btn w-8 h-8">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          )}
        </div>
      </div>

      {/* One block per homepage section, in the site's own order */}
      {!loading && unique.size === 0 ? (
        <div className="py-20 text-center text-sm text-zinc-500">
          {isSearching
            ? 'ไม่พบเรื่องที่ค้นหา ลองพิมพ์ชื่อภาษาอังกฤษ'
            : typeFilter !== 'all'
            ? 'ไม่มีเรื่องประเภทนี้ในหน้านี้'
            : 'โหลดรายการไม่สำเร็จ กดปุ่มโหลดใหม่อีกครั้ง'}
        </div>
      ) : (
        blocks.map((b) => {
          if (b.items.length === 0) return null;
          const zone = zoneTheme(b.id, b.ranked);
          const Icon = zone.icon;
          const framed = blocks.length > 1 || isSearching;
          return (
            <section key={b.id} className={framed ? 'mt-6 sm:mt-8' : 'mt-5'}>
              {framed && (
                // Stays under the app header while scrolling through the zone
                <div className="sticky top-[var(--app-header-h,56px)] z-20 -mx-3 sm:-mx-6 px-3 sm:px-6 py-3 mb-3 bg-canvas/85 backdrop-blur-xl flex items-center justify-between gap-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${zone.badge}`}>
                      <Icon className="w-4 h-4" />
                    </span>
                    <div className="min-w-0">
                      <h3 className="text-sm sm:text-[15px] font-semibold text-zinc-50 leading-tight truncate">
                        {b.label}
                        <span className="ml-2 text-xs font-normal text-zinc-500 tabular-nums">{b.items.length} เรื่อง</span>
                      </h3>
                      <p className="text-[11px] sm:text-xs text-zinc-500 truncate mt-0.5">{zone.hint}</p>
                    </div>
                  </div>
                  {!isSearching && blocks.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setSectionBySite((prev) => ({ ...prev, [site.name]: b.id }))}
                      className="text-xs font-medium text-zinc-400 hover:text-zinc-100 whitespace-nowrap shrink-0 transition-colors"
                    >
                      ดูเฉพาะหมวดนี้ ›
                    </button>
                  )}
                </div>
              )}
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-x-2.5 gap-y-5 sm:gap-x-4 sm:gap-y-7">
                {b.items.map((r) => (
                  <DiscoverCard
                    key={r.key}
                    result={r}
                    ranked={b.ranked}
                    tracked={findTrackedManga(r, mangaList)}
                    onAdd={() => setAdding([r])}
                  />
                ))}
              </div>
            </section>
          );
        })
      )}

      {adding && adding.length > 0 && (
        <AddToListSheet
          results={adding}
          onClose={() => setAdding(null)}
          onConfirm={(manga) => {
            onAddManga(manga);
            setAdding(null);
          }}
        />
      )}
    </main>
  );
};

const coverChip = 'px-1.5 py-[3px] rounded-md bg-black/60 backdrop-blur-sm text-[8.5px] sm:text-[10px] font-medium leading-none';

const DiscoverCard: React.FC<{
  result: DiscoverResult;
  ranked?: boolean;
  tracked?: Manga;
  onAdd: () => void;
}> = ({ result: r, ranked, tracked, onAdd }) => {
  const { main, sub } = splitTitle(r.title);
  const flagUrl = getFlagUrl(r.nationality);
  const detail = r.updatedAt ? `อัปเดต${formatRelativeThai(r.updatedAt)}` : '';

  return (
    <div className="flex flex-col">
      <div className="relative aspect-[3/4] rounded-lg sm:rounded-xl bg-zinc-900 overflow-hidden ring-1 ring-inset ring-white/[0.06]">
        <DiscoverCover src={r.image} alt={r.title} />
        {r.type && (
          <span className={`absolute top-1.5 left-1.5 flex items-center gap-1 text-zinc-100 ${coverChip}`}>
            {flagUrl && <img src={flagUrl} alt="" className="w-3 h-2 sm:w-3.5 sm:h-2.5 rounded-[1px] object-cover" />}
            {r.type}
          </span>
        )}
        {r.fresh && !ranked && (
          <span className="absolute top-1.5 right-1.5 px-1.5 py-[3px] rounded-md bg-sky-500 text-white text-[8.5px] sm:text-[10px] font-semibold leading-none shadow">
            UP
          </span>
        )}
        {ranked && r.rank !== undefined && (
          <span className="absolute top-1.5 right-1.5 min-w-[1.5rem] h-6 px-1.5 rounded-md bg-zinc-50 text-zinc-900 text-xs font-bold flex items-center justify-center shadow tabular-nums">
            {r.rank}
          </span>
        )}
        {r.rating !== undefined && (
          <span className={`absolute bottom-1.5 left-1.5 flex items-center gap-0.5 text-amber-300 ${coverChip}`}>
            <Star className="w-2.5 h-2.5 sm:w-3 sm:h-3 fill-amber-300" />
            {r.rating.toFixed(1)}
          </span>
        )}
        {/* Chapter badge in the corner of the cover, like mangablackcat's "24 ตอน" */}
        {r.latest > 0 && (
          <span className={`absolute bottom-1.5 right-1.5 text-zinc-100 tabular-nums ${coverChip}`}>{r.latest} ตอน</span>
        )}
      </div>

      <div className="pt-2 px-0.5 flex flex-col flex-grow gap-0.5">
        <h4 title={r.title} className="font-medium text-[11px] sm:text-[13px] text-zinc-100 truncate leading-snug">
          {main}
        </h4>
        {sub && <p className="text-[10px] sm:text-[11px] text-zinc-500 line-clamp-2 leading-snug">{sub}</p>}
        {detail && <p className="text-[10px] sm:text-[11px] text-zinc-500 truncate">{detail}</p>}
        {r.meta && <p className="text-[10px] sm:text-[11px] text-zinc-500 truncate">{r.meta}</p>}

        <div className="flex items-center gap-1 mt-auto pt-2">
          {tracked ? (
            <div
              className="flex-1 h-7 sm:h-8 rounded-lg bg-emerald-400/10 text-emerald-300 text-[10px] sm:text-xs font-medium flex items-center justify-center gap-1 min-w-0"
              title={`อยู่ใน Tier ${tracked.grade}`}
            >
              <Check className="w-3 h-3 shrink-0" />
              <span className="truncate">ในลิสต์ · {tracked.grade}</span>
            </div>
          ) : (
            <button
              type="button"
              onClick={onAdd}
              className="flex-1 h-7 sm:h-8 rounded-lg bg-zinc-50 hover:bg-white active:scale-95 text-zinc-900 text-[10px] sm:text-xs font-medium flex items-center justify-center gap-1 transition-all"
            >
              <Plus className="w-3 h-3" />
              เพิ่ม
            </button>
          )}
          <a
            href={r.url}
            target="_blank"
            rel="noopener noreferrer"
            title={`เปิดใน ${r.site}`}
            className="w-7 h-7 sm:w-8 sm:h-8 shrink-0 rounded-lg bg-white/[0.04] ring-1 ring-inset ring-white/[0.06] text-zinc-400 hover:text-zinc-100 flex items-center justify-center transition-colors"
          >
            <ExternalLink className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
          </a>
        </div>
      </div>
    </div>
  );
};

interface AddToListSheetProps {
  results: DiscoverResult[];
  onClose: () => void;
  onConfirm: (manga: Manga[]) => void;
}

/** Tier picker for one manga (with title / read chapter / nationality) or for a whole bulk add */
const AddToListSheet: React.FC<AddToListSheetProps> = ({ results, onClose, onConfirm }) => {
  const single = results.length === 1 ? results[0] : null;
  const [title, setTitle] = useState(single?.title || '');
  const [grade, setGrade] = useState<TierGrade>('EX');
  const [readCh, setReadCh] = useState(0);
  const [nationality, setNationality] = useState(single?.nationality || '🇰🇷');

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (single) {
      onConfirm([buildMangaFromDiscover(single, { title, grade, readCh: Math.max(0, readCh), nationality })]);
    } else {
      onConfirm(results.map((r, i) => buildMangaFromDiscover(r, { grade, readCh: 0 }, i + 1)));
    }
  };

  return (
    <div className="modal-overlay items-end sm:items-center p-0 sm:p-4">
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="modal-panel max-w-md rounded-b-none sm:rounded-b-2xl p-4 sm:p-5 gap-4 safe-area-bottom"
      >
        {single ? (
          <div className="flex gap-3">
            <div className="w-16 sm:w-20 aspect-[3/4] rounded-lg overflow-hidden bg-zinc-900 ring-1 ring-inset ring-white/[0.06] shrink-0">
              <DiscoverCover src={single.image} alt={single.title} />
            </div>
            <div className="flex-1 min-w-0 flex flex-col">
              <label className="field-label">ชื่อเรื่อง</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} required className="field" />
              <span className="text-[11px] text-zinc-500 mt-1.5">
                {single.site}
                {single.latest > 0 ? ` · ตอน ${single.latest}` : ''}
              </span>
            </div>
          </div>
        ) : (
          <div>
            <div className="text-sm font-semibold text-zinc-50">เพิ่ม {results.length} เรื่องเข้าลิสต์</div>
            <div className="flex gap-1 mt-2.5 overflow-hidden">
              {results.slice(0, 8).map((r) => (
                <div key={r.key} className="w-9 aspect-[3/4] rounded-md overflow-hidden bg-zinc-900 shrink-0">
                  <DiscoverCover src={r.image} alt={r.title} />
                </div>
              ))}
              {results.length > 8 && (
                <div className="w-9 aspect-[3/4] rounded-md bg-white/[0.06] text-[10px] text-zinc-300 flex items-center justify-center shrink-0">
                  +{results.length - 8}
                </div>
              )}
            </div>
          </div>
        )}

        <div>
          <div className="field-label">จัดเข้า Tier</div>
          <div className="grid grid-cols-4 sm:grid-cols-8 gap-1.5">
            {GRADES.map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setGrade(g)}
                className={`h-9 rounded-lg text-sm font-semibold transition-colors ${
                  grade === g
                    ? TIER_CONFIGS[g].solid
                    : 'bg-white/[0.04] text-zinc-400 ring-1 ring-inset ring-white/[0.06] hover:text-zinc-100'
                }`}
              >
                {g}
              </button>
            ))}
          </div>
        </div>

        {single && (
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="field-label">อ่านถึงตอน</label>
              <input
                type="number"
                min={0}
                step="any"
                value={readCh}
                onChange={(e) => setReadCh(Number(e.target.value) || 0)}
                className="field"
              />
            </div>
            <div>
              <div className="field-label">สัญชาติ</div>
              <div className="flex gap-1">
                {NATIONALITIES.map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setNationality(n)}
                    title={n}
                    className={`w-10 h-9 rounded-lg ring-1 ring-inset flex items-center justify-center transition-colors ${
                      nationality === n ? 'bg-white/10 ring-white/30' : 'bg-white/[0.03] ring-white/[0.06]'
                    }`}
                  >
                    <img src={getFlagUrl(n) as string} alt={n} className="w-5 h-3.5 rounded-[2px] object-cover" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        <p className="text-[11px] text-zinc-500 leading-relaxed">
          หลังเพิ่ม ระบบจะค้นหาเรื่องนี้ในเว็บอื่นที่เหลือ และติดตามตอนใหม่ให้อัตโนมัติ
        </p>

        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="btn btn-secondary flex-1 h-10">
            ยกเลิก
          </button>
          <button type="submit" className="btn btn-primary flex-[2] h-10">
            <Plus className="w-4 h-4" />
            {single ? `เพิ่มเข้า Tier ${grade}` : `เพิ่ม ${results.length} เรื่องเข้า Tier ${grade}`}
          </button>
        </div>
      </form>
    </div>
  );
};
